import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { Card } from '@/components/ui/card';
import { API } from '@/utils/api';
import axios from 'axios';
import { Database, FileJson, Users, Building2, Activity, Clock, Zap, Server } from 'lucide-react';

const SuperAdminHealth = ({ auth, onLogout }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/saas/system-health`).then(r => {
      setHealth(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="bg-slate-900 border-slate-800 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color || 'text-white'}`}>{value}</p>
        </div>
        <div className={`p-2.5 rounded-lg bg-slate-800`}>
          <Icon className={`w-5 h-5 ${color || 'text-slate-400'}`} />
        </div>
      </div>
    </Card>
  );

  return (
    <SuperAdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 lg:p-8 space-y-8" data-testid="superadmin-health">
        <div>
          <h1 className="text-2xl font-bold text-white">Sante systeme</h1>
          <p className="text-sm text-slate-400 mt-1">Surveillance technique de la plateforme</p>
        </div>

        {loading ? (
          <div className="text-slate-400 text-center py-20">Chargement...</div>
        ) : health && (
          <>
            {/* System stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Database} label="Collections" value={health.total_collections} color="text-blue-400" />
              <StatCard icon={FileJson} label="Documents" value={health.total_documents?.toLocaleString()} color="text-indigo-400" />
              <StatCard icon={Building2} label="Tenants actifs" value={`${health.active_tenants}/${health.total_tenants}`} color="text-emerald-400" />
              <StatCard icon={Users} label="Employes actifs" value={health.employees_count} color="text-amber-400" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top collections */}
              <Card className="bg-slate-900 border-slate-800 p-6">
                <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                  <Database className="w-4 h-4 text-blue-400" /> Top 10 collections
                </h2>
                <div className="space-y-2">
                  {(health.top_collections || []).map((c, i) => {
                    const maxCount = health.top_collections[0]?.count || 1;
                    return (
                      <div key={c.name} className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 w-5 text-right">{i + 1}</span>
                        <span className="text-sm text-slate-300 flex-1 font-mono truncate">{c.name}</span>
                        <span className="text-sm font-medium text-white w-16 text-right">{c.count.toLocaleString()}</span>
                        <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-blue-600" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Scheduled jobs */}
              <Card className="bg-slate-900 border-slate-800 p-6">
                <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-400" /> Jobs planifies
                </h2>
                <div className="space-y-3">
                  {(health.scheduled_jobs || []).map((job, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                      <Zap className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">{job.name}</p>
                        <p className="text-xs text-slate-500">{job.schedule}</p>
                      </div>
                      <span className="w-2 h-2 rounded-full bg-emerald-500" title="Actif"></span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-900 border-slate-800 p-6">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-xs text-slate-400">Activite (24h)</p>
                    <p className="text-2xl font-bold text-white">{health.recent_activity_24h} actions</p>
                  </div>
                </div>
              </Card>
              <Card className="bg-slate-900 border-slate-800 p-6">
                <div className="flex items-center gap-3">
                  <Server className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-xs text-slate-400">Heure serveur</p>
                    <p className="text-lg font-mono text-white">{new Date(health.server_time).toLocaleString('fr-CH')}</p>
                  </div>
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminHealth;
